import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useOutletContext, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import api, { unwrapList } from "utils/api";
import { API_ENDPOINTS } from "config/api";
import { extractErrorMessage } from "utils/apiError";
import { toast } from "utils/toast";
import { translations } from "utils/i18n";
import { findProjectName } from "utils/transcript";
import { useIsMobile } from "hooks/use-mobile";
import { useLanguage } from "context/languageContext";
import { IN_FLIGHT_STATUSES } from "components/transcription-list";
import { NameDialog } from "components/name-dialog";
import ProjectsPanel from "components/projects-panel";

/** Read by ProjectsPage (the index) and ProjectDetailPage — see the <Outlet
    context={...}/> below. Named after arkii-product's own useLibrarySearch,
    which uses the same layout-route-owns-the-data pattern for its panel. */
export function useProjectsContext() {
  return useOutletContext();
}

/**
 * Owns the Projects section's shared data — the project list, every
 * transcription (for per-project counts and each project's file list) — so
 * the panel and whichever page is open never drift out of sync. Ported from
 * arkii-product's LibraryLayout, which does the same for its LibraryPanel.
 */
export default function ProjectsLayout() {
  const { id } = useParams();
  const isMobile = useIsMobile();
  // On mobile there's no room for the panel and a detail pane side by side
  // (see ProjectsPanel), so only one shows at a time — same list-then-detail
  // pattern as a mail app. ProjectDetailPage's own back chevron is what gets
  // you from the detail pane back to the panel.
  const showPanel = !isMobile || !id;
  const showContent = !isMobile || !!id;
  const { language } = useLanguage();
  const trList = translations[language].transcriptionList;
  const trLayout = translations[language].projectsLayout;
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get(API_ENDPOINTS.PROJECT.LIST), api.get(API_ENDPOINTS.TRANSCRIPTION.HISTORY)])
      .then(([projectRes, historyRes]) => {
        if (cancelled) return;
        setProjects(unwrapList(projectRes.data));
        setItems(unwrapList(historyRes.data));
      })
      .catch((err) => {
        if (!cancelled) toast.error(extractErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshItems = useCallback(async () => {
    const { data } = await api.get(API_ENDPOINTS.TRANSCRIPTION.HISTORY);
    setItems(unwrapList(data));
  }, []);

  const upsertItem = useCallback((item) => {
    setItems((current) => {
      const exists = current.some((candidate) => candidate.id === item.id);
      return exists
        ? current.map((candidate) => (candidate.id === item.id ? { ...candidate, ...item } : candidate))
        : [item, ...current];
    });
  }, []);

  const pollRef = useRef(null);
  useEffect(() => {
    const hasInFlight = items.some((t) => IN_FLIGHT_STATUSES.includes(t.status));
    if (hasInFlight && !pollRef.current) {
      pollRef.current = setInterval(() => refreshItems().catch(() => {}), 3000);
    }
    if (!hasInFlight && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [items, refreshItems]);

  const counts = useMemo(() => {
    const tally = {};
    for (const t of items) {
      if (t.project) tally[t.project] = (tally[t.project] ?? 0) + 1;
    }
    return tally;
  }, [items]);

  async function createProject(name) {
    try {
      const { data } = await api.post(API_ENDPOINTS.PROJECT.LIST, { name });
      setProjects((prev) => [data, ...prev]);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function renameProject(id, name) {
    try {
      const { data } = await api.patch(API_ENDPOINTS.PROJECT.DETAIL(id), { name });
      setProjects((prev) => prev.map((p) => (p.id === id ? data : p)));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function deleteProject(id) {
    try {
      await api.delete(API_ENDPOINTS.PROJECT.DETAIL(id));
      setProjects((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(extractErrorMessage(err));
      throw err;
    }
  }

  async function deleteItem(itemId) {
    try {
      await api.delete(API_ENDPOINTS.TRANSCRIPTION.DETAIL(itemId));
      setItems((prev) => prev.filter((t) => t.id !== itemId));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function renameItem(itemId, name) {
    try {
      const { data } = await api.patch(API_ENDPOINTS.TRANSCRIPTION.DETAIL(itemId), { name });
      setItems((prev) => prev.map((t) => (t.id === itemId ? { ...t, name: data.name ?? name } : t)));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  // silent skips the per-item success toast — used by bulk imports, which
  // show their own single "Imported N" summary instead of one toast per
  // file. Errors still always surface, bulk or not.
  async function moveItemToProject(item, projectId, { silent = false } = {}) {
    try {
      const { data } = await api.patch(API_ENDPOINTS.TRANSCRIPTION.DETAIL(item.id), { project: projectId });
      setItems((prev) => prev.map((t) => (t.id === item.id ? { ...t, project: data.project } : t)));
      if (!silent)
        toast.success(
          projectId ? trList.movedToProject(findProjectName(projects, projectId)) : trList.removedFromProject
        );
      return true;
    } catch (err) {
      toast.error(extractErrorMessage(err));
      return false;
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-1">
      {showPanel && (
        <ProjectsPanel
          projects={projects}
          counts={counts}
          loading={loading}
          onCreateClick={() => setCreateOpen(true)}
          fullWidth={isMobile}
        />
      )}

      {showContent && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex h-full min-h-0 flex-1 items-center justify-center">
              <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden="true" />
            </div>
          ) : (
            <Outlet
              context={{
                projects,
                items,
                counts,
                refreshItems,
                upsertItem,
                renameProject,
                deleteProject,
                deleteItem,
                renameItem,
                moveItemToProject,
                openCreateProject: () => setCreateOpen(true),
              }}
            />
          )}
        </div>
      )}

      <NameDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={trLayout.createProjectTitle}
        label={trLayout.nameLabel}
        submitLabel={trLayout.createLabel}
        onSubmit={createProject}
      />
    </div>
  );
}
